// The API loads server/lib/googleCalendarLink.js. Keep that copy aligned with this file.
// Build calendar handoff links and .ics files from an extracted school event.
// No Calendar API: the parent taps to add the entry in Google or Outlook.
// Event shape (from the extraction pipeline):
//   { title, date: "YYYY-MM-DD", time: "HH:MM"|null, all_day: bool,
//     end_date: "YYYY-MM-DD"|null, child, action_type, source_quote }

const GCAL_BASE = "https://calendar.google.com/calendar/render";
const OUTLOOK_BASE = "https://outlook.office.com/calendar/action/compose";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function parseYmd(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

function ymdCompact(ymd) {
  return `${ymd.year}${pad2(ymd.month)}${pad2(ymd.day)}`;
}

function ymdIso(ymd) {
  return `${ymd.year}-${pad2(ymd.month)}-${pad2(ymd.day)}`;
}

/** Clock time the parent reviewed, without a UTC suffix (Outlook reads that in the mailbox timezone). */
function localIsoDateTime(ymd, hm) {
  return `${ymdIso(ymd)}T${pad2(hm.hour)}:${pad2(hm.minute)}:00`;
}

function addMinutesLocal(ymd, hm, minutes) {
  const start = new Date(ymd.year, ymd.month - 1, ymd.day, hm.hour, hm.minute, 0);
  const end = new Date(start.getTime() + minutes * 60_000);
  return {
    ymd: { year: end.getFullYear(), month: end.getMonth() + 1, day: end.getDate() },
    hm: { hour: end.getHours(), minute: end.getMinutes() },
  };
}

/** Add n days to a {year,month,day}, returning a new compact-safe parts object. */
function addDays(ymd, n) {
  const d = new Date(Date.UTC(ymd.year, ymd.month - 1, ymd.day));
  d.setUTCDate(d.getUTCDate() + n);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

function parseHm(value) {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(value || ""));
  if (!m) return null;
  return { hour: Number(m[1]), minute: Number(m[2]) };
}

/** Local timed datetime -> compact UTC stamp "YYYYMMDDTHHMMSSZ" for Google Calendar. */
function localToUtcStamp(ymd, hm, durationMin = 60) {
  const start = new Date(ymd.year, ymd.month - 1, ymd.day, hm.hour, hm.minute, 0);
  const end = new Date(start.getTime() + durationMin * 60_000);
  const fmt = (d) =>
    `${d.getUTCFullYear()}${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}T${pad2(
      d.getUTCHours()
    )}${pad2(d.getUTCMinutes())}00Z`;
  return { start: fmt(start), end: fmt(end) };
}

function buildDetails(event) {
  const parts = [];
  if (event?.source_quote) parts.push(`„${event.source_quote}"`);
  if (event?.child) parts.push(`Kind: ${event.child}`);
  parts.push("Erkannt von Albis aus einer KiTa/Kindergarten-E-Mail.");
  return parts.join("\n\n");
}

/**
 * Build the dates= parameter. Timed events use a UTC start/end window; all-day
 * events use YYYYMMDD/<next-day> (Google's all-day end is exclusive).
 * @returns {string|null}
 */
function buildDatesParam(event) {
  const start = parseYmd(event?.date);
  if (!start) return null;
  const hm = event?.all_day === false ? parseHm(event?.time) : null;
  if (hm) {
    const stamps = localToUtcStamp(start, hm);
    return `${stamps.start}/${stamps.end}`;
  }
  const endYmd = inclusiveEndYmd(event, start);
  const exclusiveEnd = addDays(endYmd, 1);
  return `${ymdCompact(start)}/${ymdCompact(exclusiveEnd)}`;
}

/**
 * Inclusive last day when it falls after the start. Timed events stay one slot.
 * @param {object} event
 * @param {{ year: number, month: number, day: number }} start
 */
function inclusiveEndYmd(event, start) {
  if (event?.all_day === false && parseHm(event?.time)) return start;
  const end = parseYmd(event?.end_date);
  if (!end || ymdCompact(end) <= ymdCompact(start)) return start;
  return end;
}

/**
 * Inclusive end date (YYYY-MM-DD) for a multi-day all-day event, or null.
 * @param {object} event
 * @returns {string|null}
 */
export function spanEndDate(event) {
  const start = parseYmd(event?.date);
  if (!start) return null;
  const end = inclusiveEndYmd(event, start);
  if (ymdCompact(end) === ymdCompact(start)) return null;
  return ymdIso(end);
}

/** Phone Calendar app claims calendar.google.com and splits a raw slash out of the query. */
function isMobileCalendarClient() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
}

/**
 * "Add to Google Calendar" URL, or null when the event has no usable date.
 * On a phone the date slash stays encoded so the app keeps the range.
 * On desktop the date slash is literal so the website prefills the event.
 * @param {object} event
 * @param {{ encodeDateSlash?: boolean }} [options]
 *   encodeDateSlash keeps %2F so mail clients and the phone Calendar app keep the range.
 * @returns {string|null}
 */
export function buildGoogleCalendarUrl(event, { encodeDateSlash = false } = {}) {
  const dates = buildDatesParam(event);
  if (!dates) return null;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event?.title || "Termin",
    details: buildDetails(event),
    dates,
  });
  let query = params.toString();
  if (!encodeDateSlash && !isMobileCalendarClient()) {
    // Desktop ignores %2F and opens an empty calendar. Only the dates value needs a raw slash.
    query = query.replace(`dates=${encodeURIComponent(dates)}`, `dates=${dates}`);
  }
  return `${GCAL_BASE}?${query}`;
}

/**
 * "Add to Outlook" compose URL, or null when the event has no usable date.
 * Timed events use the local clock time (no Z). All-day end is exclusive,
 * matching the .ics and Google ranges.
 * @param {object} event
 * @returns {string|null}
 */
export function buildOutlookCalendarUrl(event) {
  const start = parseYmd(event?.date);
  if (!start) return null;
  const hm = event?.all_day === false ? parseHm(event?.time) : null;

  let startdt;
  let enddt;
  let allday;
  if (hm) {
    const end = addMinutesLocal(start, hm, 60);
    startdt = localIsoDateTime(start, hm);
    enddt = localIsoDateTime(end.ymd, end.hm);
    allday = "false";
  } else {
    const endYmd = inclusiveEndYmd(event, start);
    const exclusiveEnd = addDays(endYmd, 1);
    startdt = ymdIso(start);
    enddt = ymdIso(exclusiveEnd);
    allday = "true";
  }

  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: event?.title || "Termin",
    body: buildDetails(event),
    startdt,
    enddt,
    allday,
  });
  return `${OUTLOOK_BASE}?${params.toString()}`;
}

function icsEscape(value) {
  return String(value || "")
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

/**
 * Minimal single-event .ics document, or null when the event has no usable date.
 * @param {object} event
 * @returns {string|null}
 */
export function buildIcs(event) {
  const start = parseYmd(event?.date);
  if (!start) return null;
  const hm = event?.all_day === false ? parseHm(event?.time) : null;

  let dtStart;
  let dtEnd;
  if (hm) {
    const stamps = localToUtcStamp(start, hm);
    dtStart = `DTSTART:${stamps.start}`;
    dtEnd = `DTEND:${stamps.end}`;
  } else {
    const endYmd = inclusiveEndYmd(event, start);
    const exclusiveEnd = addDays(endYmd, 1);
    dtStart = `DTSTART;VALUE=DATE:${ymdCompact(start)}`;
    dtEnd = `DTEND;VALUE=DATE:${ymdCompact(exclusiveEnd)}`;
  }

  const now = new Date();
  const stamp = `${now.getUTCFullYear()}${pad2(now.getUTCMonth() + 1)}${pad2(
    now.getUTCDate()
  )}T${pad2(now.getUTCHours())}${pad2(now.getUTCMinutes())}${pad2(now.getUTCSeconds())}Z`;
  const uid = `${stamp}-${Math.random().toString(36).slice(2, 10)}@albis`;

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Albis//School Email//DE",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    dtStart,
    dtEnd,
    `SUMMARY:${icsEscape(event?.title || "Termin")}`,
    `DESCRIPTION:${icsEscape(buildDetails(event))}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

/** Object URL for downloading an event's .ics (browser only), or null. */
export function buildIcsDownloadUrl(event) {
  const ics = buildIcs(event);
  if (!ics || typeof window === "undefined" || !window.Blob) return null;
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  return URL.createObjectURL(blob);
}
